import React from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useParams, useNavigate } from "react-router-dom"

import Button from '@mui/material/Button'
import LoginIcon from '@mui/icons-material/Login'
import HomeIcon from '@mui/icons-material/HomeRounded'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import Link from '@mui/material/Link'
import { Icon as IconifyIcon, InlineIcon as IconifyIconInline } from "@iconify/react"
import { Grid, CircularProgress, Alert } from '@mui/material'

import { setToolbar } from '../store/toolbar'
import { setBreadcrumbs } from '../store/breadcrumbs'

import API from '../api/fetchAPI'
import { fetchDashboardStats } from '../api/fetchDashboardData'

import DashboardStats from '../components/dashboard/DashboardStats'
import SubmissionsByLab from '../components/dashboard/SubmissionsByLab'
import ClassificationChart from '../components/dashboard/ClassificationChart'
import TrendChart from '../components/dashboard/TrendChart'
import RecentActivity from '../components/dashboard/RecentActivity'
import VKGLClassificationChart from '../components/dashboard/VKGLClassificationChart'
import ConsensusOverview from '../components/dashboard/ConsensusOverview'
import ConflictingVariantsTable from '../components/dashboard/ConflictingVariantsTable'
import ConflictingGenesChart from '../components/dashboard/ConflictingGenesChart'
import ClinVarImport from '../components/ClinVarImport'

import './Home.scss'


const API_DOCS_URL = process.env.NODE_ENV === 'development' 
    ? 'http://localhost:9000/api/v1/docs'
    : '/api/v1/docs'

function LinkBullet() {
    return (
        // <IconifyIcon className="icon" icon="iconamoon:arrow-right-2-bold"/>
        <IconifyIcon className="icon" icon="ph:caret-double-right-duotone"/>
        // <IconifyIcon className="icon" icon="ph:caret-right-duotone"/>
        // <IconifyIcon className="icon" icon="pepicons-print:arrow-right"/>
        // <IconifyIcon className="icon" icon="ph:arrow-fat-right-duotone"/>
        // <IconifyIcon className="icon" icon="ph:arrow-fat-line-right-duotone"/>
    )
}





export default function Page1() {

    const navigate = useNavigate()
    const user = useSelector((state) => state.user)

    const dispatch = useDispatch()

    const [outcome, setOutcome] = React.useState('')
    const [dashboardStats, setDashboardStats] = React.useState(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState(null)

    const renderToolbar = () => null

    const breadcrumbs = [
        {
            key: 'home',
            label: 'Home',
            path: '/home',
            icon: HomeIcon
        }
    ]

    React.useEffect(() => {
        dispatch(setBreadcrumbs(breadcrumbs))
        dispatch(setToolbar(renderToolbar()))
        loadDashboardData()
        
        // Auto-refresh every 5 minutes
        const interval = setInterval(loadDashboardData, 5 * 60 * 1000)
        return () => clearInterval(interval)
    }, [])

    const loadDashboardData = async () => {
        try {
            setLoading(true)
            setError(null)
            const stats = await fetchDashboardStats()
            setDashboardStats(stats)
        } catch (err) {
            console.error('Failed to load dashboard data:', err)
            setError('Dashboard-Daten konnten nicht geladen werden')
        } finally {
            setLoading(false)
        }
    }

    // const bla = () => {
    //     // dispatch(setUser({ username: 'ASchmid', name: 'Alex Schmid', isSuperuser: true }))
    //     API.post('/api/test/neu', {
    //         doNotThrowFor: [401]
    //     }).then(data => {
    //         setOutcome("OK")
    //     }).catch(error => {
    //         setOutcome("ERROR")
    //     })
    // }

    // create error 
    // const ui = null.do()

    return (
        
        <div className="page-content home-content home-page">

            <h2>Willkommen in der HGQN Fall- und Variantendatenbank</h2>

            <p>
                Excel Template für den Upload neuer Fälle und Varianten
                <Link className="link" href="https://uni-bonn.sciebo.de/s/n3DwCeNXPGanUYc/download">
                <LinkBullet/>
                    Download Excel Template
                </Link>
            </p>

            <p>
                Informationen zu "Datendienste für genetische Varianten"
                <Link className="link" target="'blank" href="https://docs.google.com/document/d/1X6uLEUBcxA01pO1kvibA6LGukxV8fsrTV2wSnrKw0fw/export?format=pdf">
                    <LinkBullet/>
                    FAQ - Datendienste für genetische Varianten
                </Link>
            </p>

            <p>
                Haftungsausschluss und Richtlinien zur Datennutzung
                <Link className="link" href="#" onClick={() => navigate('/disclaimer')}>
                    <LinkBullet/>
                    Haftungsausschluss und Richtlinien zur Datennutzung
                </Link>
            </p>

            <p>
                <span>
                {/* TODO: remove badge once feature is established */}
                    <span className="new-feature-badge">New Feature</span>Interaktive REST API Dokumentation
                </span>
                <Link className="link" href={API_DOCS_URL} target="_blank" rel="noopener noreferrer">
                    <LinkBullet/>
                    API Dokumentation
                </Link>
                
            </p>

            <ClinVarImport onImported={loadDashboardData} />
            {dashboardStats && <p>{dashboardStats.placeholderCases || 0} offene ClinVar-Platzhalter (nicht als Patientenfälle gezählt)</p>}

            {/* Dashboard Section */}
            <div className="dashboard-container">
                <h3 className="dashboard-title">Dashboard</h3>

                {loading && (
                    <div className="loading-container">
                        <CircularProgress />
                        <p>Dashboard wird geladen...</p>
                    </div>
                )}

                {error && (
                    <Alert severity="error" className="error-alert">
                        {error}
                    </Alert>
                )}

                {!loading && !error && dashboardStats && (
                    <>
                        <DashboardStats stats={dashboardStats} />

                        <Grid container spacing={3} className="charts-grid">
                            <Grid item xs={12} lg={6}>
                                <SubmissionsByLab data={dashboardStats.submissionsByLab} />
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <ClassificationChart data={dashboardStats.classificationDistribution} />
                            </Grid>
                            {/*<Grid item xs={12} lg={8}>*/}
                            {/*    <TrendChart data={dashboardStats.monthlyTrend} />*/}
                            {/*</Grid>*/}
                            {/*<Grid item xs={12} lg={4}>*/}
                            {/*    <RecentActivity activities={dashboardStats.recentActivity} />*/}
                            {/*</Grid>*/}
                        </Grid>

                        {/* Consensus Analysis Section (Fokkema et al. style) */}
                        <div className="consensus-section">
                            <h3 className="dashboard-title" style={{ marginTop: '40px' }}>
                                Konsensus-Analyse (Multi-Labor Variantenklassifikation)
                            </h3>

                            {/* Main VKGL Chart - Prominent Display */}
                            <VKGLClassificationChart data={dashboardStats.vkglChartData} />

                            {/* Consensus Metrics */}
                            <ConsensusOverview stats={dashboardStats.consensusStats} />

                            {/* Detailed Conflict Analysis */}
                            <Grid container spacing={3} className="conflict-analysis-grid">
                                <Grid item xs={12} lg={6}>
                                    <ConflictingVariantsTable data={dashboardStats.conflictingVariantsList} />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <ConflictingGenesChart data={dashboardStats.conflictingGenes} />
                                </Grid>
                            </Grid>
                        </div>
                    </>
                )}
            </div>

        </div>
    )
}
